export type ListViewColumnField =
  | 'type'
  | 'size'
  | 'dateModified'
  | 'dateCreated'
  | 'dateUploaded'
  | 'dateTaken'
  | 'uploadedBy'
  | 'ext'
  | 'resolution'
  | 'tagCount'
  | 'boardCount'
  | 'openCommentCount';

export type ListViewColumn = {
  field: ListViewColumnField;
  label: string;
  sortable: boolean;
  sortFieldName: string;
  width: number;
};

export const LIST_VIEW_BLANK_CELL = '--';

export const LIST_VIEW_TITLE_MIN_WIDTH = 240;

export const getListViewGridTemplateColumns = (visibleColumns: ListViewColumnField[]) =>
  `minmax(${LIST_VIEW_TITLE_MIN_WIDTH}px, 1fr) ${visibleColumns
    .map((field) => {
      const { width } = getListViewColumn(field);

      return `minmax(${width}px, ${width}px)`;
    })
    .join(' ')}`;

export const getListViewMinWidth = (visibleColumns: ListViewColumnField[]) =>
  LIST_VIEW_TITLE_MIN_WIDTH + visibleColumns.reduce((total, field) => total + getListViewColumn(field).width, 0);

export const LIST_VIEW_DEFAULT_VISIBLE_COLUMNS: ListViewColumnField[] = ['type', 'size', 'dateModified'];

// Widths mirror apps/core TableView/types.ts getTableColumnWidth — tuned for
// short values (type, size, ext) vs longer labels (dates, uploaded by).
// Re-sync if core table widths change.
export const LIST_VIEW_COLUMNS: ListViewColumn[] = [
  { field: 'type', label: 'Type', sortable: true, sortFieldName: 'type', width: 90 },
  { field: 'size', label: 'Size', sortable: true, sortFieldName: 'size', width: 90 },
  { field: 'dateModified', label: 'Date modified', sortable: true, sortFieldName: 'dateModified', width: 170 },
  { field: 'dateCreated', label: 'Date created', sortable: true, sortFieldName: 'dateCreated', width: 160 },
  { field: 'dateUploaded', label: 'Date uploaded', sortable: true, sortFieldName: 'dateUploaded', width: 190 },
  { field: 'dateTaken', label: 'Date taken', sortable: true, sortFieldName: 'dateTaken', width: 160 },
  { field: 'uploadedBy', label: 'Uploaded by', sortable: true, sortFieldName: 'uploadedBy', width: 220 },
  { field: 'ext', label: 'Extension', sortable: true, sortFieldName: 'ext', width: 115 },
  { field: 'resolution', label: 'Resolution', sortable: false, sortFieldName: 'resolution', width: 125 },
  { field: 'tagCount', label: 'Tag count', sortable: true, sortFieldName: 'tagCount', width: 110 },
  { field: 'boardCount', label: 'Board count', sortable: true, sortFieldName: 'boardCount', width: 120 },
  { field: 'openCommentCount', label: 'Comment count', sortable: true, sortFieldName: 'openCommentCount', width: 150 },
];

export const getListViewColumn = (field: ListViewColumnField): ListViewColumn => {
  const column = LIST_VIEW_COLUMNS.find((entry) => entry.field === field);

  if (!column) {
    throw new Error(`Unknown list view column: ${field}`);
  }

  return column;
};

export type ListViewSort = {
  direction: 'asc' | 'desc';
  name: string;
};

export const LIST_VIEW_DEFAULT_SORT: ListViewSort = {
  direction: 'desc',
  name: 'dateModified',
};

export const LIST_VIEW_NAME_SORT_COLUMN: ListViewColumn = {
  field: 'type',
  label: 'Name',
  sortable: true,
  sortFieldName: 'name',
  width: LIST_VIEW_TITLE_MIN_WIDTH,
};

export const getDefaultListSortDirection = (fieldName: string) =>
  ['name', 'size', 'ext', 'type', 'uploadedBy'].includes(fieldName) ? ('asc' as const) : ('desc' as const);

export const getToggledListViewSort = (sort: ListViewSort, fieldName: string): ListViewSort => {
  const isActive = sort.name === fieldName;

  if (!isActive) {
    return { direction: getDefaultListSortDirection(fieldName), name: fieldName };
  }

  return {
    direction: sort.direction === 'desc' ? 'asc' : 'desc',
    name: fieldName,
  };
};
